import { useForm } from "react-hook-form"
import { useTimeMachine } from "@/contexts/TimeMachineContext"
import { useProcess } from "@/contexts/ProcessContext"
import { useCreateAttendanceEvent } from "@/entities/attendanceEvent/useAttendanceEvent"
import { useYoungsByGroup } from "@/entities/user/useYoung"
import { addMinutes, endOfDay, format, startOfDay } from "date-fns"
import { User } from "@/entities/user/user"
import { useEffect } from "react"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { AttendanceTime } from "@/types/AttendanceTime"
import { DateTimeSelector } from "@/components/form/DateTimeSelector"
import RecurringFields from "@/components/form/RecurringFields"
import { isDevEnv } from "@/config/env"
import { runTransactionBatch } from "@/firestore/transactions"
import { createOneOrRecurringAttendanceEvents } from "@/controller/recurringAttendanceEvents"
import { v4 } from "uuid"
import { useCurrentSchoolYear } from "@/entities/schoolYear/useSchoolYear"

const fromTimeOptions = 
	 [
		"start_of_day",
		"morning_break",
		"lunch_break",
		"afternoon_break",
		"specific_time",
	] as AttendanceTime[]

const toTimeOptions = 
   [
     "morning_break",
     "lunch_break",
     "afternoon_break",
     "end_of_day",
     "specific_time",
  ] as AttendanceTime[]


type FormValues = z.infer<typeof formSchema>

const formSchema = z.object({
   type: z.enum(["present", "late", "absent", "external"] as const),
   from: z.string(),
   to: z.string(),
   is_recurring: z.boolean().optional(),
   recurring_period: z.enum(["everyday", "everyweek", "everymonth"]).optional(),
   recurring_end_type: z.enum(["school_year_end", "custom_date"]).optional(),
   recurring_end_date: z.date().optional(),
   justified: z.boolean(),
   applyToGroup: z.boolean().optional(),
}).refine((data) => {
   if (data.type === "late") return true
   
   const fromDate = new Date(data.from)
   const toDate = new Date(data.to)
   
   return !isNaN(fromDate.getTime()) && !isNaN(toDate.getTime()) && fromDate <= toDate
}, {
   message: "La date de fin doit être après la date de début",
   path: ["to"]
}).refine((data) => {
   if (data.is_recurring && data.recurring_end_type === "custom_date") {
     return !!data.recurring_end_date;
   }
   return true;
 }, {
   message: "Veuillez sélectionner une date de fin de récurrence",
   path: ["recurring_end_date"],
})

interface Props {
   young: User
   schoolYearId: string
   onClose: () => void
}

export function AddEventContent({ young, schoolYearId, onClose }: Props) {
   const createAttendanceEvent = useCreateAttendanceEvent()
   const { now } = useTimeMachine()
   const { data: currentSchoolYear } = useCurrentSchoolYear()
   const { startProcess } = useProcess()
   const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<FormValues>({
      resolver: zodResolver(formSchema),
      defaultValues: {
         type: "absent",
         from: format(startOfDay(now), "yyyy-MM-dd'T'HH:mm"),
         to: format(endOfDay(now), "yyyy-MM-dd'T'HH:mm"),
         justified: false,
         applyToGroup: false,
         ...(isDevEnv && {
            // is_recurring: true,
         }),
      },
   })

   const type = watch("type")
   const from = watch("from")
   const to = watch("to")

   const showJustifiedCheckbox = type === "absent" || type === "late"

   const { data: youngsInGroup } = useYoungsByGroup(young.group)

   const onSubmit = handleSubmit((data) => {
      startProcess(async () => {
         const dataFrom = new Date(data.from)
         const dataTo = new Date(data.to)
         const finalJustified = showJustifiedCheckbox ? data.justified : true
         const finalJustificationValidationStatus = finalJustified ? "validated" : "upload_pending"

         let from = dataFrom
         let to = dataTo

         if (data.type === "external") {
            from = startOfDay(from)
            to = endOfDay(to)
         }

         if (data.type === "late") {
            to = addMinutes(from, 15)
         }

         const delayMinutes = data.type === "late" ? 15 : 0

         if (data.is_recurring) {                     
            if (data.recurring_end_type === "school_year_end") {
               data.recurring_end_date = currentSchoolYear?.end_date
            } else if (data.recurring_end_type === "custom_date") {
               data.recurring_end_date = endOfDay(data.recurring_end_date!)
            }
         }

         const createEvent = async (targetYoungId: string) => {
            await createOneOrRecurringAttendanceEvents({
               is_recurring: data.is_recurring || false,
               recurring_period: data.recurring_period!,
               recurring_end_date: data.recurring_end_date ? data.recurring_end_date : new Date(),
               recurring_group: v4(),
               type: data.type,
               from,
               to,
               delayMinutes,
               justified: finalJustified,
               young_id: targetYoungId,
               school_year_id: schoolYearId,
               justification_validation_status: finalJustificationValidationStatus,
               admin_comment: "",
               expected: true,
               createAttendanceEvent,
            })
         }

         await runTransactionBatch(async () => {
            if (data.type === "external" && data.applyToGroup && youngsInGroup) {
               await Promise.all(youngsInGroup.map((young) => createEvent(young.id)))
            } else {
               await createEvent(young.id)
            }
         })

         onClose()
      })
   })

   const is_recurring = watch("is_recurring")

   return (
      <div className="">
         <h1 className="text-2xl font-bold mb-2">Ajouter un événement</h1>
         <form onSubmit={onSubmit} className="space-y-4">
            <div className="form-control w-full">
               <label className="label">
                  <span className="label-text">Type d'événement</span>
               </label>
               <select className="select select-bordered w-full" {...register("type")}>
                  <option value="present">Présent</option>
                  <option value="late">Retard</option>
                  <option value="absent">Absence</option>
                  <option value="external">Extérieur</option>
               </select>
            </div>

            <div className="form-control w-full">
               <label className="label">
                  <span className="label-text">Début</span>
               </label>
               <DateTimeSelector
                  initialDateValue={startOfDay(now)}
                  onChange={(date: Date | null) => setValue("from", date ? date.toISOString() : "")}
                  enabledTimeOptions={type === "external" ? ["start_of_day"] : fromTimeOptions}
                  error={errors.from?.message}
               />
               
            </div>

            {type !== "late" && (
               <div className="form-control w-full">
                  <label className="label">
                     <span className="label-text">Fin</span>
                  </label>
                  <DateTimeSelector
                     initialDateValue={endOfDay(now)}
                     minDateValue={new Date(from)}
                     onChange={(date: Date | null) => setValue("to", date ? date.toISOString() : "")}
                     enabledTimeOptions={type === "external" ? ["end_of_day"] : toTimeOptions}
                     initialTimeOption="end_of_day"
                     error={errors.to?.message}
                  />
                
               </div>
            )}

            <RecurringFields
               titleNoun="l'événement"
               register={register}
               watch={watch}
               errors={errors}
            />

            {type === "external" && (
               <div className="form-control">
                  <label className="label cursor-pointer">
                     <span className="label-text">
                        Appliquer {is_recurring ? "ces événements" : "cet événement"} à tout le groupe {young.group} ?
                     </span>
                     <input type="checkbox" className="checkbox" {...register("applyToGroup")} />
                  </label>
               </div>
            )}

            {showJustifiedCheckbox && (
               <div className="form-control">
                  <label className="label cursor-pointer">
                     <span className="label-text">
                        {is_recurring ?
                        "Ces événements sont-ils déjà justifiés ?" :
                        "Cet événement est-il déjà justifié ?"}
                     </span>
                     <input type="checkbox" className="checkbox" {...register("justified")} />
                  </label>
               </div>
            )}

            <p className="text-sm text-gray-600 text-center mt-6">
               Aucun email ou SMS ne sera envoyé.
            </p>

            <div className="modal-action">
               <button type="button" className="btn" onClick={onClose}>
                  Annuler
               </button>
               <button type="submit" className="btn btn-primary">
                  Ajouter
               </button>
            </div>
         </form>
      </div>
   )
}
